import type { ReactNode } from 'react'
import { Card, cn } from './ui'

/**
 * ResponsiveTable（Unit 011）
 *
 * Admin 系の全テーブルで共有する表示専用コンポーネント。
 * - `md` 以上: セマンティックな `<table>`
 * - `md` 未満: 行ごとの `Card`（縦積み）
 *
 * レンダリング戦略は **CSS ベースの二重レンダリング**。テーブル DOM とカード DOM を
 * 常に両方レンダリングし、Tailwind のレスポンシブ `hidden`（`hidden md:table` / `md:hidden`）で
 * 表示のみを切替える。`matchMedia`/JS 分岐やテスト専用 prop は持たない。
 * これにより jsdom は両 DOM を評価でき、両出力テストが安定する。
 *
 * データ取得・業務ロジック・状態は持たない（親が確定済み `rows` とハンドラを渡す）。
 */

export type CellAlign = 'left' | 'right' | 'center'

export interface Column<T> {
  /** 列の一意キー */
  key: string
  /** テーブルヘッダ表示 */
  header: ReactNode
  /** セル描画 */
  render: (row: T) => ReactNode
  /** セルの整列（既定 left） */
  align?: CellAlign
  /** モバイルカードでの項目ラベル（未指定なら header を使用） */
  mobileLabel?: ReactNode
}

export interface ResponsiveTableProps<T> {
  columns: Column<T>[]
  rows: T[]
  /** 行の一意キー */
  rowKey: (row: T) => string
  /** 行アクション（プラン変更 Select・操作ボタン等）。省略時は操作列なし */
  rowActions?: (row: T) => ReactNode
  /** 操作列の見出し（既定 '操作'）。rowActions 指定時に使用 */
  actionsHeader?: ReactNode
  /** 操作列/カード内アクションの整列（既定 right） */
  actionsAlign?: CellAlign
  /** 0 件時の表示 */
  emptyMessage?: ReactNode
  /** 合計等のサマリ。tfoot（デスクトップ）/ Card（モバイル）に同一内容で描画 */
  footer?: ReactNode
  /** a11y 用テーブルキャプション（スクリーンリーダー向け） */
  caption?: string
  className?: string
}

const ALIGN_CLASS: Record<CellAlign, string> = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
}

export function ResponsiveTable<T>({
  columns,
  rows,
  rowKey,
  rowActions,
  actionsHeader = '操作',
  actionsAlign = 'right',
  emptyMessage = 'データがありません',
  footer,
  caption,
  className,
}: ResponsiveTableProps<T>) {
  const hasActions = Boolean(rowActions)
  const totalCols = columns.length + (hasActions ? 1 : 0)
  const isEmpty = rows.length === 0

  return (
    <div className={cn('w-full', className)}>
      {/* デスクトップ: テーブル（md 以上で表示） */}
      <table className="hidden md:table w-full border-collapse">
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr className="border-b border-gray-200">
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                className={cn(
                  'py-3 px-4 font-medium text-text-sub',
                  ALIGN_CLASS[col.align ?? 'left'],
                )}
              >
                {col.header}
              </th>
            ))}
            {hasActions && (
              <th scope="col" className={cn('py-3 px-4 font-medium text-text-sub', ALIGN_CLASS[actionsAlign])}>
                {actionsHeader}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {isEmpty ? (
            <tr>
              <td colSpan={totalCols} className="py-8 text-center text-text-sub">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={rowKey(row)} className="border-b border-gray-100 hover:bg-gray-50">
                {columns.map((col) => (
                  <td key={col.key} className={cn('py-3 px-4', ALIGN_CLASS[col.align ?? 'left'])}>
                    {col.render(row)}
                  </td>
                ))}
                {hasActions && (
                  <td className={cn('py-3 px-4', ALIGN_CLASS[actionsAlign])}>{rowActions!(row)}</td>
                )}
              </tr>
            ))
          )}
        </tbody>
        {footer && (
          <tfoot>
            <tr className="border-t border-gray-200 font-bold">
              <td colSpan={totalCols} className="py-3 px-4">
                {footer}
              </td>
            </tr>
          </tfoot>
        )}
      </table>

      {/* モバイル: 行カード（md 未満で表示） */}
      <div className="md:hidden space-y-3">
        {isEmpty ? (
          <Card padding="sm" className="text-center text-text-sub">
            {emptyMessage}
          </Card>
        ) : (
          rows.map((row) => (
            <Card key={rowKey(row)} padding="sm" className="space-y-2">
              {columns.map((col) => (
                <div key={col.key} className="flex items-start justify-between gap-3">
                  <span className="text-sm font-medium text-text-sub shrink-0">
                    {col.mobileLabel ?? col.header}
                  </span>
                  <span className={cn('text-sm min-w-0', ALIGN_CLASS[col.align ?? 'right'])}>
                    {col.render(row)}
                  </span>
                </div>
              ))}
              {hasActions && (
                <div className="pt-2 border-t border-gray-100 space-y-2">
                  <span className="text-sm font-medium text-text-sub">{actionsHeader}</span>
                  <div className={cn('flex flex-wrap items-center gap-2', actionsAlign === 'right' && 'justify-end')}>
                    {rowActions!(row)}
                  </div>
                </div>
              )}
            </Card>
          ))
        )}
        {footer && (
          <Card padding="sm" className="font-bold">
            {footer}
          </Card>
        )}
      </div>
    </div>
  )
}
